import { SERVICE_NAME, TVL_TIERS } from '@/constants/shared';
import {
  AmplitudeTrackingEvent,
  AmplitudeUserGroups,
  AmplitudeUserProperties,
  resetAmplitudeTracking,
  setAmplitudeUserGroup,
  setAmplitudeUserId,
  setAmplitudeUserProperty,
  trackAmplitudeEvent,
} from '@/lib/amplitude';
import { useAppKitEvents, useWalletInfo } from '@reown/appkit/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useAuth } from './useAuth';
import { useCurrentUserData } from './useCurrentUserData';
import { useUser } from './useUser';

const getHighestTvlTier = (tvl: number) => {
  return Math.max(...TVL_TIERS.filter((tier) => tier < tvl));
};
export const useAnalytics = () => {
  const [searchParams] = useSearchParams();
  const [utmSource] = useState(searchParams.get('utm_source'));
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const { authedViaLogin, address } = useAuth();
  const { user } = useUser();

  const events = useAppKitEvents();
  const { walletInfo } = useWalletInfo();
  const { currentUserData } = useCurrentUserData();
  useEffect(() => {
    setAmplitudeUserProperty(AmplitudeUserProperties.TVLBucket, getHighestTvlTier(currentUserData?.stats.TVL ?? 0));
  }, [currentUserData?.stats.TVL]);

  useEffect(() => {
    if (address) {
      setAmplitudeUserId(address);
    } else {
      resetAmplitudeTracking();
    }
    setAmplitudeUserProperty(AmplitudeUserProperties.WalletAddress, address);
  }, [address]);

  useEffect(() => {
    setAmplitudeUserProperty(AmplitudeUserProperties.WalletName, walletInfo?.name ?? 'unknown');
  }, [walletInfo?.name]);

  useEffect(() => {
    if (user?.role) setAmplitudeUserGroup(AmplitudeUserGroups.Role, user.role);
  }, [user?.role]);

  useEffect(() => {
    if (authedViaLogin) {
      trackAmplitudeEvent(AmplitudeTrackingEvent.LoginSuccess, {
        wallet_name: walletInfo?.name ?? 'unknown',
        referred_user: !!currentUserData?.stats.referredBy,
        plume_product: SERVICE_NAME,
      });
    }
    // We only want to track this info when the auth state changes
    // we expect wallet info to change when a user disconnects
    // but that shouldn't trigger this hook to fire again
    // this hook should only fire again when the logout process is complete
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedViaLogin]);
  useEffect(() => {
    if (events.data.event === 'CONNECT_SUCCESS') {
      trackAmplitudeEvent(AmplitudeTrackingEvent.ConnectedWallet, {
        wallet_name: walletInfo?.name ?? 'unknown',
        referred_user: !!currentUserData?.stats.referredBy,
        plume_product: SERVICE_NAME,
      });
    }
    // We only want to track this info when a wallet event fires
    // user information changing shouldn't retrigger his hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);
  useEffect(() => {
    // Ignore hashchange events from iframes
    if (location.pathname.startsWith('/')) {
      const previousPath = previousPathRef.current;
      trackAmplitudeEvent(AmplitudeTrackingEvent.PageView, {
        url_path: location.pathname,
        plume_product: SERVICE_NAME,
        previous_url_path: previousPath,
        utm_source: utmSource,
      });
      previousPathRef.current = location.pathname;
    }
  }, [location, utmSource]);
};
